import { alsoDrivesOf, cardPayOnlineLabel, parseMoneyToCents, qrPayLabel, tt, type MoneyInput } from '@agos/shared'

/**
 * The day's takings against the wallets that are supposed to hold them.
 *
 * `/summaries` puts a headline figure at the top — 1 039,90 € on 28 July in the
 * restored data — and a table of employee wallets underneath, and never says
 * whether the second adds up to the first. On that day it does not:
 *
 * | | day | wallets | gap |
 * |---|---|---|---|
 * | total | 1 039,90 € | 997,05 € | **42,85 €** |
 * | cash | 386,40 € | 371,90 € | 14,50 € |
 * | online | 194,10 € | 165,75 € | 28,35 € |
 *
 * Both figures come from the same rows with the same status rule
 * (`NOT IN ('cancelled','pending')`) and the same effective-date rule, so the gap is
 * not a rounding difference or a different definition of the day. It is **orders
 * attributed to nobody**: a delivery with no `driver_id`, a table order with no
 * `employee_id`. 32,95 € of delivery revenue and 9,90 € of restaurant/bar revenue,
 * that day, that belong to no shift.
 *
 * A manager reading a wallet table under a headline total reasonably assumes the
 * rows account for it. FB-075 is that they do not, and this file computes by how
 * much.
 */

export interface DaySummary {
  orderCount?: number
  totalRevenue?: MoneyInput
  cashTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
  /**
   * A QR payment taken at the door - §9.741, BACKEND.md §10A.390. Absent from a server that predates it,
   * which reads as nought; and `otherPaymentTotal` stopped holding these at the same time.
   */
  qrPayTotal?: MoneyInput
  otherPaymentTotal?: MoneyInput
  totalDiscount?: MoneyInput
  totalDelivery?: MoneyInput
  avgOrder?: MoneyInput
  cancelledCount?: number
  cancelledTotal?: MoneyInput
  byChannel?: ChannelRevenue[]
}

export interface ChannelRevenue {
  slug?: string
  name?: string
  orderCount?: number
  revenue?: MoneyInput
}

export interface WalletEmployee {
  id?: number
  name?: string
  position?: string
  systemRole?: string
  isExternal?: boolean
  walletTotal?: MoneyInput
  orderCount?: number
  cashTotal?: MoneyInput
  cardTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
  /** §9.741 - a door QR payment, which went to the bank rather than to whoever carried the order. */
  qrPayTotal?: MoneyInput
}

export interface WalletTotals {
  totalCents: number
  cashCents: number
  cardTerminalCents: number
  cardCourierCents: number
  cardOnlineCents: number
  qrPayCents: number
  orderCount: number
}

/** Only the people with orders — legacy hides the rest, and an empty row adds nothing. */
export function withOrders(wallets: WalletEmployee[]): WalletEmployee[] {
  return wallets.filter((wallet) => (wallet.orderCount ?? 0) > 0)
}

/**
 * The waiters, or the people who deliver.
 *
 * §9.686, owner #25 - **a marked account that is not a waiter is filed with the drivers.** `GET /employees/wallets`
 * lists whoever the main admin marked *Jazdí aj rozvoz* since §10A.345, so a marked main admin comes back with the
 * role `main_admin` - and a split by role alone would put their delivery cash in neither table while the
 * reconciliation still counted it. A marked waiter stays with the waiters: their row answers both columns.
 */
export function byRole(wallets: WalletEmployee[], role: 'waiter' | 'driver'): WalletEmployee[] {
  return withOrders(wallets).filter((wallet) =>
    role === 'waiter'
      ? wallet.systemRole === 'waiter'
      : wallet.systemRole === 'driver' || (alsoDrivesOf(wallet) && wallet.systemRole !== 'waiter'),
  )
}

export function walletTotals(wallets: WalletEmployee[]): WalletTotals {
  const sum = (pick: (wallet: WalletEmployee) => MoneyInput): number =>
    wallets.reduce((total, wallet) => total + parseMoneyToCents(pick(wallet)), 0)

  return {
    totalCents: sum((wallet) => wallet.walletTotal),
    cashCents: sum((wallet) => wallet.cashTotal),
    cardTerminalCents: sum((wallet) => wallet.cardTerminalTotal),
    cardCourierCents: sum((wallet) => wallet.cardCourierTotal),
    cardOnlineCents: sum((wallet) => wallet.cardOnlineTotal),
    qrPayCents: sum((wallet) => wallet.qrPayTotal),
    orderCount: wallets.reduce((total, wallet) => total + (wallet.orderCount ?? 0), 0),
  }
}

export interface Reconciliation {
  dayCents: number
  walletsCents: number
  /** The day's takings that belong to no employee. Never negative in practice. */
  unattributedCents: number
  /** Where the gap is, by payment method. Only the non-zero ones. */
  gaps: { label: string; cents: number }[]
  reconciles: boolean
}

/** A cent is a discrepancy; there is nothing below it to round. */
const TOLERANCE_CENTS = 0

/**
 * How much of the day nobody is carrying.
 *
 * Reported per payment method as well as in total, because which method it is says
 * what to look for: unattributed **cash** is money somebody physically has and no
 * shift is accountable for, while unattributed **online** is money that arrived
 * without an order ever being assigned. Only the first is a handover problem.
 *
 * **A door QR payment is a line of its own** - §9.741, BACKEND.md §10A.390: like the
 * online card it reached the bank and nobody's hand, and without its line a QR order
 * with nobody on it would leave the per-method gaps short of the total gap, with
 * nothing on the screen saying where the rest is.
 */
export function reconcile(summary: DaySummary | undefined, wallets: WalletEmployee[]): Reconciliation {
  const totals = walletTotals(withOrders(wallets))
  const dayCents = parseMoneyToCents(summary?.totalRevenue)

  const gaps = [
    { label: tt('Hotovosť'), cents: parseMoneyToCents(summary?.cashTotal) - totals.cashCents },
    {
      label: tt('Karta – terminál'),
      cents: parseMoneyToCents(summary?.cardTerminalTotal) - totals.cardTerminalCents,
    },
    {
      label: tt('Karta – kuriér'),
      cents: parseMoneyToCents(summary?.cardCourierTotal) - totals.cardCourierCents,
    },
    {
      // §9.767 - the online card's one name; this gap said *Online*.
      label: cardPayOnlineLabel(),
      cents: parseMoneyToCents(summary?.cardOnlineTotal) - totals.cardOnlineCents,
    },
    {
      label: qrPayLabel(),
      cents: parseMoneyToCents(summary?.qrPayTotal) - totals.qrPayCents,
    },
  ].filter((gap) => Math.abs(gap.cents) > TOLERANCE_CENTS)

  const unattributedCents = dayCents - totals.totalCents

  return {
    dayCents,
    walletsCents: totals.totalCents,
    unattributedCents,
    gaps,
    reconciles: Math.abs(unattributedCents) <= TOLERANCE_CENTS,
  }
}

/**
 * The channel split the backend already sends and legacy never shows.
 *
 * §10A.1 gave a franchise its own sales channels, so the *channel's* name wins over
 * the three hardcoded type labels legacy maps — which is how "BAR" comes out as
 * `BAR` rather than as *Osobný odber*.
 */
export function channelLines(summary: DaySummary | undefined): { name: string; orderCount: number; cents: number }[] {
  return (summary?.byChannel ?? []).map((channel) => ({
    name: channel.name ?? channel.slug ?? '',
    orderCount: channel.orderCount ?? 0,
    cents: parseMoneyToCents(channel.revenue),
  }))
}

/**
 * `1 objednávka`, `2 objednávky`, `0` and `5+` `objednávok`.
 *
 * Legacy's ternary is `count === 1 ? … : count < 5 ? "objednávky" : "objednávok"`,
 * which reads a quiet day as *0 objednávky*. Slovak puts zero in the genitive
 * plural with the fives, not with the twos.
 */
export function orderWord(count: number): string {
  if (count === 1) return tt('objednávka')
  if (count >= 2 && count <= 4) return tt('objednávky')
  return tt('objednávok')
}

export function initialsOf(name: string | undefined): string {
  return (name ?? '')
    .split(' ')
    .map((word) => word[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/** Biggest wallet first — the one somebody is about to hand over most from. */
export function byWallet(wallets: WalletEmployee[]): WalletEmployee[] {
  return [...wallets].sort(
    (left, right) => parseMoneyToCents(right.walletTotal) - parseMoneyToCents(left.walletTotal),
  )
}
